import { PERMISSIONS } from "@/constants/permissions";
import { useHasPermission } from "@/hooks/usePermissions";
import { queryClient } from "@/queryClient";
import type {
  CheckBulkPermissionsCheck,
  CheckBulkPermissionsRequest,
  CheckPermissionRequest,
  PermissionCheckResult,
  PermissionCheckResultFormatted,
  Relationship,
  ResourceObjectType,
} from "@cohesive/api-client";
import { Organization, OrganizationUser } from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback } from "react";
import { orgAPI } from "./api";
import { Group, useGroups } from "./groups";
import { useUserInfo } from "./user";
import { useUsers } from "./users";

// Type aliases for backward compatibility
export type CheckPermissionPayload = CheckPermissionRequest;
export type CheckBulkPermissionsPayload = CheckBulkPermissionsRequest;

export function isCheckBulkPermissionsCheck(
  payload: any,
): payload is CheckBulkPermissionsCheck {
  return (
    payload &&
    "permission" in payload &&
    "resource_object_id" in payload &&
    "resource_object_type" in payload
  );
}

export const canAccess = (objectType: string, objectId?: string): boolean => {
  if (!objectId) return false;
  return !!queryClient.getQueryData<boolean>([
    "permission",
    objectType,
    objectId,
    "can_access",
  ]);
};

export const canEdit = (objectType: string, objectId?: string): boolean => {
  if (!objectId) return false;
  return !!queryClient.getQueryData<boolean>([
    "permission",
    objectType,
    objectId,
    "can_edit",
  ]);
};

export const canDelete = (objectType: string, objectId?: string): boolean => {
  if (!objectId) return false;
  return !!queryClient.getQueryData<boolean>([
    "permission",
    objectType,
    objectId,
    "can_delete",
  ]);
};

export const canView = (objectType: string, objectId?: string): boolean => {
  if (!objectId) return false;
  return !!queryClient.getQueryData<boolean>([
    "permission",
    objectType,
    objectId,
    "can_view",
  ]);
};

export const canManageAssignments = (
  objectType: string,
  objectId?: string,
): boolean => {
  if (!objectId) return false;
  return !!queryClient.getQueryData<boolean>([
    "permission",
    objectType,
    objectId,
    "can_manage_assignments",
  ]);
};

export const isOwner = (objectType: string, objectId?: string): boolean => {
  if (!objectId) return false;
  return !!queryClient.getQueryData<boolean>([
    "permission",
    objectType,
    objectId,
    "owner",
  ]);
};

export function useCreateRelationship(
  options?: UseMutationOptions<boolean, AxiosError, Relationship>,
) {
  const queryClient = useQueryClient();
  return useMutation<boolean, AxiosError, Relationship>({
    mutationFn: async (payload) => {
      return await orgAPI.relations.createRelationship(payload);
    },
    mutationKey: ["createRelationship"],
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: [
          "relationships",
          payload.resource_object_type,
          payload.resource_object_id,
        ],
      });
      // Sharing a file record changes who sees it under "Shared with me".
      if (payload.resource_object_type === "file_record") {
        queryClient.invalidateQueries({
          queryKey: ["fileRecords", "sharedWithMe"],
        });
      }
    },
    ...options,
  });
}

export function useDeleteRelationship(
  options?: UseMutationOptions<boolean, AxiosError, Relationship>,
) {
  const queryClient = useQueryClient();
  return useMutation<boolean, AxiosError, Relationship>({
    mutationFn: async (payload) => {
      return await orgAPI.relations.deleteRelationship(payload);
    },
    mutationKey: ["deleteRelationship"],
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: [
          "relationships",
          payload.resource_object_type,
          payload.resource_object_id,
        ],
      });
      // Unsharing a file record changes who sees it under "Shared with me".
      if (payload.resource_object_type === "file_record") {
        queryClient.invalidateQueries({
          queryKey: ["fileRecords", "sharedWithMe"],
        });
      }
    },
    ...options,
  });
}

export function useListRelationships<T = Relationship>(
  resourceObjectType: ResourceObjectType,
  resourceObjectId: string,
  options?: Partial<UseQueryOptions<T[], AxiosError>>,
) {
  return useQuery<T[], AxiosError>({
    queryKey: ["relationships", resourceObjectType, resourceObjectId],
    queryFn: async () => {
      const relationships = await orgAPI.relations.listRelationships(
        resourceObjectType,
        resourceObjectId,
      );
      return relationships as T[];
    },
    enabled: Boolean(resourceObjectId && resourceObjectType),
    ...options,
  });
}

export function useCheckPermission(
  payload: CheckPermissionRequest,
  options?: UseQueryOptions<boolean, AxiosError>,
) {
  return useQuery<boolean, AxiosError>({
    queryKey: [
      "permission",
      payload.resource_object_type,
      payload.resource_object_id,
      payload.permission,
    ],
    queryFn: async () => {
      return await orgAPI.relations.checkPermission(payload);
    },
    enabled: Boolean(
      payload.resource_object_id &&
        payload.resource_object_type &&
        payload.permission,
    ),
    ...options,
  });
}

export function useCheckBulkPermissions(
  payload: CheckBulkPermissionsRequest,
  options?: Partial<
    UseQueryOptions<
      PermissionCheckResult[],
      AxiosError,
      PermissionCheckResultFormatted
    >
  >,
) {
  return useQuery<
    PermissionCheckResult[],
    AxiosError,
    PermissionCheckResultFormatted
  >({
    queryKey: ["checkBulkPermissions", payload],
    queryFn: async () => {
      const response = await orgAPI.relations.checkBulkPermissions(payload);

      // Cache individual permission results
      response.data.permissions.forEach((permission: PermissionCheckResult) => {
        queryClient.setQueryData(
          [
            "permission",
            permission.resource_object_type,
            permission.resource_object_id,
            permission.permission,
          ],
          permission.has_permission,
        );
      });

      return response.data.permissions;
    },
    enabled: payload.checks.length > 0,
    select: (data): PermissionCheckResultFormatted => {
      const formatted: PermissionCheckResultFormatted = {
        group: {},
        folder: {},
        skill: {},
        meeting_transcript: {},
        canvas: {},
        workspace: {},
        file_record: {},
      };

      data.forEach((permission) => {
        const {
          resource_object_type,
          resource_object_id,
          permission: permissionType,
        } = permission;

        if (!formatted[resource_object_type][resource_object_id]) {
          formatted[resource_object_type][resource_object_id] = {
            can_access: false,
            can_view: false,
            can_edit: false,
            can_delete: false,
            can_manage_assignments: false,
            owner: false,
          };
        }

        // @ts-ignore
        formatted[resource_object_type][resource_object_id][permissionType] =
          permission.has_permission;
      });

      return formatted;
    },
    ...options,
  });
}

export interface RelationshipWithResource extends Relationship {
  resource?: Group | OrganizationUser | Organization;
}

export function useListRelationshipsWithResources(
  resourceObjectType: ResourceObjectType,
  resourceObjectId: string,
  options?: Partial<UseQueryOptions<RelationshipWithResource[], AxiosError>>,
) {
  const isOrgAdmin = useHasPermission(PERMISSIONS.ORG_ADMIN.ALL);

  // Fetch data from different sources
  const { data: userInfo } = useUserInfo();
  const { data: groups = [] } = useGroups({
    enabled: isOrgAdmin,
  });
  const { data: users = [] } = useUsers({
    enabled: isOrgAdmin,
  });

  const getRelationshipResource = useCallback(
    (
      objectType: string,
      objectId: string,
    ): Group | OrganizationUser | Organization | undefined => {
      switch (objectType) {
        case "organization":
          return userInfo?.organization;
        case "organization_user":
          return users.find((user) => user.id === objectId);
        case "group":
          return groups.find((group) => group.id === objectId);
        default:
          return undefined;
      }
    },
    [userInfo, users, groups],
  );

  return useListRelationships<RelationshipWithResource>(
    resourceObjectType,
    resourceObjectId,
    {
      select: (relationships): RelationshipWithResource[] => {
        return relationships.map((relationship) => {
          return {
            ...relationship,
            resource: getRelationshipResource(
              relationship.subject_object_type,
              relationship.subject_object_id,
            ),
          };
        });
      },
      enabled: Boolean(resourceObjectId && resourceObjectType),
      ...options,
    },
  );
}

export function isOrganizationUser(
  resource: Group | OrganizationUser | Organization | undefined,
): resource is OrganizationUser {
  return typeof resource !== "undefined" && "email" in resource;
}

export function isOrganization(
  resource: Group | OrganizationUser | Organization | undefined,
): resource is Organization {
  return (
    typeof resource !== "undefined" && "name" in resource && "slug" in resource
  );
}
